import * as Sentry from "@sentry/nextjs"
import type { SeverityLevel } from "@sentry/nextjs"

/**
 * Reporta a Sentry un error de PostgREST/Postgres con un título que se lee.
 *
 * Por qué existe (auditoría 2026-10-07, P1-4): supabase-js devuelve el error
 * como un objeto `{ code, message, details, hint }`, no como un `Error`. Pasado
 * tal cual a `Sentry.captureException`, el título del issue sale del nombre de
 * clase que tenga en el bundle minificado de producción: los issues de dinero
 * REDY-35 («f»), REDY-36 («o») y REDY-2B («Y») eran un 22P05 y un ON CONFLICT
 * que nadie podía reconocer en la lista.
 *
 * Con esto el issue se llama `DbError: payment_transactions.insert 22P05
 * unsupported Unicode escape sequence` y agrupa por `[db, tabla, op, código]`:
 * el mismo fallo en el mismo lugar es UN issue, sin importar el texto.
 *
 * Sin PII (C-19): el mensaje y `details` pasan por `redactDbText`, que borra
 * los valores de fila que Postgres copia en sus mensajes (`Key (phone)=(…)`,
 * `Failing row contains (…)`, el literal después de `:`). Los nombres de
 * constraint, columna o tabla se quedan: son lo que hace falta para el
 * triage. El scrubber de `beforeSend` sigue corriendo encima.
 *
 * Módulo normal (no `"use server"`): se importa desde rutas, server actions y
 * `lib/`.
 */

export type PostgresLikeError = {
  code?: string | null
  message?: string | null
  details?: string | null
  hint?: string | null
}

export type DbErrorContext = {
  /** Tabla, vista o RPC (`payment_transactions`, `rpc:checkout_tab`). */
  table: string
  /** Operación: select | insert | update | delete | upsert | rpc | … */
  op: string
  location_id?: string | null
  location_slug?: string | null
  level?: SeverityLevel
} & Record<string, unknown>

const SQLSTATE_RE = /^[0-9A-Z]{5}$/
const PGRST_RE = /^PGRST\d+$/
const MAX_MESSAGE = 120
const MAX_TAG = 200

/** ¿Tiene la forma de un error de Postgres/PostgREST? (code SQLSTATE o PGRSTnnn + message) */
export function isPostgresLikeError(e: unknown): e is PostgresLikeError & { code: string; message: string } {
  if (!e || typeof e !== "object") return false
  const { code, message } = e as PostgresLikeError
  if (typeof message !== "string" || typeof code !== "string") return false
  return SQLSTATE_RE.test(code) || PGRST_RE.test(code)
}

/** Borra los valores de fila que Postgres copia en `message`/`details`. */
export function redactDbText(text: string | null | undefined): string | null {
  if (text === null || text === undefined) return null
  return String(text)
    .replace(/\)=\([^)]*\)/g, ")=(…)")
    .replace(/Failing row contains \(.*\)/g, "Failing row contains (…)")
    // Postgres pone el VALOR después de dos puntos (`for type uuid: "abc"`); los
    // identificadores (`constraint "uq_x"`, `column "total"`) sirven y se quedan.
    .replace(/(:\s*)"[^"]*"/g, '$1"…"')
    .replace(/(:\s*)'[^']*'/g, "$1'…'")
}

function codeOf(err: unknown): string {
  if (err && typeof err === "object") {
    const code = (err as PostgresLikeError).code
    if (typeof code === "string" && code.trim()) return code.trim()
  }
  return "unknown"
}

function messageOf(err: unknown): string {
  if (err === null || err === undefined) return ""
  if (typeof err === "object") {
    const m = (err as PostgresLikeError).message
    return typeof m === "string" ? m : ""
  }
  return String(err)
}

function truncate(s: string, max: number): string {
  return s.length > max ? `${s.slice(0, max - 1)}…` : s
}

/** El `Error` que ve Sentry: nombre fijo, mensaje `<tabla>.<op> <código> <mensaje corto>`. */
export function buildDbError(err: unknown, ctx: { table: string; op: string }): Error & { code: string } {
  const code = codeOf(err)
  const short = truncate((redactDbText(messageOf(err)) ?? "").replace(/\s+/g, " ").trim(), MAX_MESSAGE)
  const message = [`${ctx.table}.${ctx.op}`, code, short].filter(Boolean).join(" ")
  const e = new Error(message) as Error & { code: string }
  e.name = "DbError"
  e.code = code
  return e
}

export function captureDbError(err: unknown, ctx: DbErrorContext): void {
  try {
    const { table, op, level, ...rest } = ctx
    const dbError = buildDbError(err, { table, op })
    const pg = (err && typeof err === "object" ? err : {}) as PostgresLikeError

    const tags: Record<string, string> = { db_table: table, db_op: op, db_code: dbError.code }
    for (const [k, v] of Object.entries(rest)) {
      if (v === null || v === undefined) continue
      if (typeof v === "string" || typeof v === "number" || typeof v === "boolean") {
        tags[k] = truncate(String(v), MAX_TAG)
      }
    }

    Sentry.captureException(dbError, {
      level: level ?? "error",
      fingerprint: ["db", table, op, dbError.code],
      tags,
      extra: {
        ...rest,
        code: dbError.code,
        details: redactDbText(pg.details),
        hint: redactDbText(pg.hint),
      },
    })
  } catch {
    // La telemetría jamás tumba la ruta que la llama.
  }
}
